<template>
  <v-data-table
    :headers="headers"
    :items="items"
    :items-per-page="full ? 10 : 5"
    item-key="id"
    show-select
  >
    <!-- PARTNER chip -->
    <template #[`item.is_partner`]="{ item }">
      <v-chip
        small
        v-show="item.is_partner"
        class="partner-chip"
      >
        {{ item.is_partner }}
      </v-chip>
    </template>

    <!-- CLIENT NAME slot -->
    <template #[`item.companyName`]="{ item }">
      <span :title="item.companyName" style="font-size: 0.875rem !important;">
        {{ item.companyName && item.companyName.length > nameMaxLength ? item.companyName.substring(0, nameMaxLength) + '...' : item.companyName }}
      </span>
    </template>

    <!-- PARTNER NAME slot -->
    <template #[`item.partnername`]="{ item }">
      <span :title="item.partnername" style="font-size: 0.875rem !important;">
        {{ item.partnername && item.partnername.length > 16 ? item.partnername.substring(0, 16) + '...' : item.partnername }}
      </span>
    </template>

    <!-- STATUS chip -->
    <template slot="item.status" slot-scope="{ item }">
      <v-chip
        small
        outlined
        :color="getStatusColor(item.status)"
      >
        {{ item.status }}
      </v-chip>
    </template>

    <!-- CREATION DATE slot -->
    <template #[`item.timecreate`]="{ item }">
      <span :title="item.timecreate ? formatTimeAndDate(item.timecreate) : ''" style="font-size: 0.875rem !important;">
        {{ item.timecreate ? formatDateYMD(item.timecreate) : '-' }}
      </span>
    </template>

    <!-- LAST TRADE slot -->
    <template #[`item.lastTrade`]="{ item }">
      <span :title="item.lastTrade ? formatTimeAndDate(item.lastTrade) : ''" style="font-size: 0.875rem !important;">
        {{ formatRelativeTime(item.lastTrade) }}
      </span>
    </template>

    <!-- TRANSACTED VOLUME slot -->
    <template #[`item.transactedVolume`]="{ item }">
      <span :title="formatFullBRL(item.rawTransactedVolume)" style="font-size: 0.875rem !important;">
        {{ item.transactedVolume }}
      </span>
    </template>

    <!-- REVENUE slot -->
    <template #[`item.revenue`]="{ item }">
      <span :title="formatFullBRL(item.rawRevenue)" style="font-size: 0.875rem !important;">
        {{ item.revenue }}
      </span>
    </template>

    <!-- ACTION buttons -->
    <template slot="item.actions" slot-scope="{ item }">
      <div class="d-flex flex-row align-center">
        <!-- Documentation / Download button (both variants) -->
        <v-btn
          :small="!full"
          :x-small="full"
          outlined
          :loading="loadingButton === item.id"
          :color="isPartner ? 'success' : 'primary'"
          class="mr-1"
          :disabled="isBroker && !item.kyc"
          @click="$emit('documentation-download', item)"
        >
          <span class="white--text">{{ isPartner ? 'Upload' : 'Download' }}</span>
        </v-btn>

        <!-- Edit -->
        <v-btn
          data-testid="edit-client"
          :small="!full"
          :x-small="full"
          color="primary"
          class="mr-1"
          :disabled="!isBroker"
          @click="$emit('edit-client', item)"
        >
          {{ $t('instructions.edit') }}
        </v-btn>

        <!-- Pause / Resume — combined ternary for normal, separate v-if for full -->
        <template v-if="full">
          <v-btn
            :disabled="!isBroker"
            x-small
            color="warning"
            class="mr-1"
            v-if="item.status === 'Active' || item.status === 'Ativo'"
            @click="$emit('toggle-client-status', { id: item.id, newStatus: 'Paused' })"
          >
            {{ $t('instructions.pause') }}
          </v-btn>
          <v-btn
            :disabled="!isBroker"
            x-small
            color="success"
            class="mr-1"
            v-if="item.status === 'Paused' || item.status === 'Pausado'"
            @click="$emit('toggle-client-status', { id: item.id, newStatus: 'Active' })"
          >
            {{ $t('instructions.resume') }}
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            :disabled="!isBroker"
            small
            :color="item.status === 'Active' || item.status === 'Ativo' ? 'warning' : 'success'"
            class="mr-1"
            @click="(item.status === 'Active' || item.status === 'Ativo') ?
              $emit('toggle-client-status', { id: item.id, newStatus: 'Paused' }) :
              $emit('toggle-client-status', { id: item.id, newStatus: 'Active' })"
          >
            {{ item.status === 'Active' || item.status === 'Ativo' ? $t('instructions.pause') : $t('instructions.resume') }}
          </v-btn>
        </template>

        <!-- Delete -->
        <v-btn
          :disabled="!isBroker"
          :small="!full"
          :x-small="full"
          color="error"
          @click="$emit('open-delete-dialog', item.id)"
        >
          {{ $t('instructions.delete') }}
        </v-btn>
      </div>
    </template>
  </v-data-table>
</template>

<script>
import * as utils from '@/utils.js'

export default {
  name: 'ClientPortfolioTable',

  props: {
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    full: {
      type: Boolean,
      default: false,
    },
    isBroker: {
      type: Boolean,
      default: false,
    },
    isPartner: {
      type: Boolean,
      default: false,
    },
    loadingButton: {
      default: null,
    },
  },

  emits: ['edit-client', 'toggle-client-status', 'open-delete-dialog', 'documentation-download'],

  computed: {
    nameMaxLength () {
      return this.full ? 64 : 32
    },
  },

  methods: {
    getStatusColor (status) {
      const s = status.toLowerCase()
      if (s === 'active' || s === 'ativo') return 'green'
      if (s === 'deal' || s === 'negocio') return 'green'
      if (s === 'paused' || s === 'pausado') return 'orange lighten-3'
      if (s === 'pending auth' || s === 'pendente autenticação') return 'blue lighten-4'
      if (s === 'pending' || s === 'pendente') return 'orange darken-2'
      return 'grey darken-1'
    },

    formatRelativeTime (val) {
      if (!val) return '-'
      let timestamp
      if (!isNaN(val)) {
        timestamp = Number(val) * 1000
      } else {
        timestamp = Date.parse(val)
      }
      if (isNaN(timestamp)) return '-'
      const diffMs = new Date() - new Date(timestamp)
      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60))

      if (diffHrs < 1) return this.$t('broker.clientPortfolio.lessThanHour')
      if (diffHrs === 1) return this.$t('broker.clientPortfolio.oneHour')
      if (diffHrs < 24) return this.$t('broker.clientPortfolio.hours', { n: diffHrs })
      return this.formatDateYMD(timestamp / 1000)
    },

    formatTimeAndDate (timestamp) {
      return utils.formatTimeAndDate(timestamp)
    },

    formatDateYMD (timestamp) {
      return utils.formatDateYMD(timestamp)
    },

    formatFullBRL (val) {
      if (val === null || val === undefined) return '-'
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(val)
    },
  },
}
</script>
